import React from 'react';
import { DocsLayout } from '../../components/docs/DocsLayout';
import { CodeBlock } from '../../components/docs/CodeBlock';
import { Callout } from '../../components/docs/Callout';
import { ArrowRight } from 'lucide-react';
export function WebhooksGuidePage() {
  const webhookHandlerCode = {
    node: `// Express.js example
app.post('/webhooks/obseq', express.json(), (req, res) => {
  const event = req.body;
  
  // Return 200 immediately
  res.status(200).json({ received: true });
  
  // Process asynchronously
  processWebhookAsync(event);
});

async function processWebhookAsync(event) {
  console.log('Event type:', event.type);
  console.log('Event ID:', event.id);
  
  // Handle specific event types
  switch (event.type) {
    case 'payments.payout.succeeded':
      await handlePayoutSuccess(event.data.object);
      break;
    case 'payments.payout.failed':
      await handlePayoutFailure(event.data.object);
      break;
  }
}`,
    python: `# Flask example
@app.route('/webhooks/obseq', methods=['POST'])
def handle_webhook():
    event = request.json
    
    # Return 200 immediately
    response = jsonify({'received': True})
    
    # Process asynchronously (use Celery, RQ, etc.)
    process_webhook_async.delay(event)
    
    return response, 200

def process_webhook_async(event):
    print(f"Event type: {event['type']}")
    print(f"Event ID: {event['id']}")
    
    # Handle specific event types
    if event['type'] == 'payments.payout.succeeded':
        handle_payout_success(event['data']['object'])
    elif event['type'] == 'payments.payout.failed':
        handle_payout_failure(event['data']['object'])`,
    go: `func handleWebhook(w http.ResponseWriter, r *http.Request) {
    var event WebhookEvent
    if err := json.NewDecoder(r.Body).Decode(&event); err != nil {
        http.Error(w, err.Error(), http.StatusBadRequest)
        return
    }
    
    // Return 200 immediately
    w.WriteHeader(http.StatusOK)
    json.NewEncoder(w).Encode(map[string]bool{"received": true})
    
    // Process asynchronously
    go processWebhookAsync(event)
}

func processWebhookAsync(event WebhookEvent) {
    log.Printf("Event type: %s", event.Type)
    log.Printf("Event ID: %s", event.ID)
    
    // Handle specific event types
    switch event.Type {
    case "payments.payout.succeeded":
        handlePayoutSuccess(event.Data.Object)
    case "payments.payout.failed":
        handlePayoutFailure(event.Data.Object)
    }
}`,
    php: `<?php
// Slim/Laravel example
$app->post('/webhooks/obseq', function ($request, $response) {
    $event = json_decode($request->getBody(), true);
    
    // Return 200 immediately
    $response->getBody()->write(json_encode(['received' => true]));
    
    // Process asynchronously (use queue)
    dispatch(new ProcessWebhook($event));
    
    return $response->withStatus(200);
});

function processWebhookAsync($event) {
    error_log("Event type: " . $event['type']);
    error_log("Event ID: " . $event['id']);
    
    // Handle specific event types
    switch ($event['type']) {
        case 'payments.payout.succeeded':
            handlePayoutSuccess($event['data']['object']);
            break;
        case 'payments.payout.failed':
            handlePayoutFailure($event['data']['object']);
            break;
    }
}`,
    ruby: `# Sinatra/Rails example
post '/webhooks/obseq' do
  event = JSON.parse(request.body.read)
  
  # Return 200 immediately
  status 200
  { received: true }.to_json
  
  # Process asynchronously (use Sidekiq, etc.)
  ProcessWebhookJob.perform_async(event)
end

def process_webhook_async(event)
  puts "Event type: #{event['type']}"
  puts "Event ID: #{event['id']}"
  
  # Handle specific event types
  case event['type']
  when 'payments.payout.succeeded'
    handle_payout_success(event['data']['object'])
  when 'payments.payout.failed'
    handle_payout_failure(event['data']['object'])
  end
end`,
    java: `// Spring Boot example
@PostMapping("/webhooks/obseq")
public ResponseEntity<Map<String, Boolean>> handleWebhook(
    @RequestBody WebhookEvent event
) {
    // Return 200 immediately
    Map<String, Boolean> response = new HashMap<>();
    response.put("received", true);
    
    // Process asynchronously
    CompletableFuture.runAsync(() -> processWebhookAsync(event));
    
    return ResponseEntity.ok(response);
}

private void processWebhookAsync(WebhookEvent event) {
    System.out.println("Event type: " + event.getType());
    System.out.println("Event ID: " + event.getId());
    
    // Handle specific event types
    switch (event.getType()) {
        case "payments.payout.succeeded":
            handlePayoutSuccess(event.getData().getObject());
            break;
        case "payments.payout.failed":
            handlePayoutFailure(event.getData().getObject());
            break;
    }
}`,
    dotnet: `// ASP.NET Core example
[HttpPost("/webhooks/obseq")]
public async Task<IActionResult> HandleWebhook([FromBody] WebhookEvent webhookEvent)
{
    // Return 200 immediately
    var response = new { received = true };
    
    // Process asynchronously
    _ = Task.Run(() => ProcessWebhookAsync(webhookEvent));
    
    return Ok(response);
}

private async Task ProcessWebhookAsync(WebhookEvent webhookEvent)
{
    Console.WriteLine($"Event type: {webhookEvent.Type}");
    Console.WriteLine($"Event ID: {webhookEvent.Id}");
    
    // Handle specific event types
    switch (webhookEvent.Type)
    {
        case "payments.payout.succeeded":
            await HandlePayoutSuccess(webhookEvent.Data.Object);
            break;
        case "payments.payout.failed":
            await HandlePayoutFailure(webhookEvent.Data.Object);
            break;
    }
}`,
    curl: `# Test webhook endpoint
curl -X POST http://localhost:3000/webhooks/obseq \\
  -H "Content-Type: application/json" \\
  -d '{
    "id": "evt_test_123",
    "type": "payments.payout.succeeded",
    "created_at": "2026-01-03T18:41:22Z",
    "data": {
      "object": {
        "id": "po_123",
        "status": "succeeded"
      }
    }
  }'`
  };
  return <DocsLayout breadcrumbs={[{
    label: 'Docs',
    href: '/docs'
  }, {
    label: 'Guides',
    href: '/docs/guides'
  }, {
    label: 'Webhooks',
    href: '/docs/guides/webhooks'
  }]} onThisPage={[{
    title: 'Overview',
    id: 'overview'
  }, {
    title: 'Webhook Payload',
    id: 'payload'
  }, {
    title: 'Event Taxonomy',
    id: 'events'
  }, {
    title: 'Handling Flow',
    id: 'handling-flow'
  }, {
    title: 'Signature Verification',
    id: 'verification'
  }, {
    title: 'Testing Webhooks',
    id: 'testing'
  }, {
    title: 'Code Examples',
    id: 'examples'
  }]} data-id="element-2181">
      <h1 data-id="element-2182">Webhooks</h1>
      <p className="lead" data-id="element-2183">
        Obseq sends webhooks to notify your application of events in real-time,
        such as successful payouts or failed transactions.
      </p>

      <h2 id="overview" data-id="element-2184">Overview</h2>
      <p data-id="element-2185">
        Webhooks are HTTP POST requests sent to your configured endpoint when
        events occur. Your endpoint must:
      </p>
      <ul data-id="element-2186">
        <li data-id="element-2187">Return a 2xx status code quickly (within 5 seconds)</li>
        <li data-id="element-2188">Process events asynchronously to avoid timeouts</li>
        <li data-id="element-2189">Handle duplicate events idempotently</li>
      </ul>

      <Callout type="tip" data-id="element-2190">
        Configure webhook endpoints in the{' '}
        <a href="#/docs/api-reference/connect" className="text-blue-600 hover:text-blue-700" data-id="element-2191">
          Connect API
        </a>{' '}
        or your dashboard.
      </Callout>

      <h2 id="payload" data-id="element-2192">Webhook Payload</h2>
      <p data-id="element-2193">All webhook events follow this structure:</p>
      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto my-4" data-id="element-2194">
        <code data-id="element-2195">{`{
  "id": "evt_01J9Z9A9Y8Y7R7F8N4W9N6W3G2",
  "type": "payments.payout.succeeded",
  "created_at": "2026-01-03T18:41:22Z",
  "api_version": "v1",
  "request_id": "req_01J9Z9A9Y8ZK2K2FQK9Z0X8C1D",
  "data": {
    "object": {
      "id": "po_01J9Z98K5K8C0MZ9QK3G4B1E2P",
      "object": "payments.payout",
      "status": "succeeded",
      "amount": 250000,
      "currency": "USD",
      ...
    }
  }
}`}</code>
      </pre>

      <h3 data-id="element-2196">Fields</h3>
      <table className="w-full text-sm my-4" data-id="element-2197">
        <thead data-id="element-2198">
          <tr className="border-b border-slate-200" data-id="element-2199">
            <th className="text-left py-2 pr-4" data-id="element-2200">Field</th>
            <th className="text-left py-2" data-id="element-2201">Description</th>
          </tr>
        </thead>
        <tbody data-id="element-2202">
          <tr className="border-b border-slate-100" data-id="element-2203">
            <td className="py-2 pr-4" data-id="element-2204">
              <code data-id="element-2205">id</code>
            </td>
            <td className="py-2" data-id="element-2206">Unique event identifier</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-2207">
            <td className="py-2 pr-4" data-id="element-2208">
              <code data-id="element-2209">type</code>
            </td>
            <td className="py-2" data-id="element-2210">Event type (product.resource.action)</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-2211">
            <td className="py-2 pr-4" data-id="element-2212">
              <code data-id="element-2213">created_at</code>
            </td>
            <td className="py-2" data-id="element-2214">ISO 8601 timestamp when event occurred</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-2215">
            <td className="py-2 pr-4" data-id="element-2216">
              <code data-id="element-2217">api_version</code>
            </td>
            <td className="py-2" data-id="element-2218">API version used for the event</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-2219">
            <td className="py-2 pr-4" data-id="element-2220">
              <code data-id="element-2221">request_id</code>
            </td>
            <td className="py-2" data-id="element-2222">
              Original API request that triggered the event
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-2223">
            <td className="py-2 pr-4" data-id="element-2224">
              <code data-id="element-2225">data.object</code>
            </td>
            <td className="py-2" data-id="element-2226">Snapshot of the resource at event time</td>
          </tr>
        </tbody>
      </table>

      <h2 id="events" data-id="element-2227">Event Taxonomy</h2>
      <p data-id="element-2228">
        Events follow the naming pattern: <code data-id="element-2229">product.resource.action</code>
      </p>

      <h3 data-id="element-2230">Orchestrate</h3>
      <ul className="space-y-1 my-3" data-id="element-2231">
        <li data-id="element-2232">
          <code data-id="element-2233">orchestrate.execution.created</code>
        </li>
        <li data-id="element-2234">
          <code data-id="element-2235">orchestrate.execution.updated</code>
        </li>
        <li data-id="element-2236">
          <code data-id="element-2237">orchestrate.execution.completed</code>
        </li>
        <li data-id="element-2238">
          <code data-id="element-2239">orchestrate.execution.failed</code>
        </li>
      </ul>

      <h3 data-id="element-2240">Payments</h3>
      <ul className="space-y-1 my-3" data-id="element-2241">
        <li data-id="element-2242">
          <code data-id="element-2243">payments.collection.created</code>
        </li>
        <li data-id="element-2244">
          <code data-id="element-2245">payments.collection.succeeded</code>
        </li>
        <li data-id="element-2246">
          <code data-id="element-2247">payments.collection.failed</code>
        </li>
        <li data-id="element-2248">
          <code data-id="element-2249">payments.payout.created</code>
        </li>
        <li data-id="element-2250">
          <code data-id="element-2251">payments.payout.succeeded</code>
        </li>
        <li data-id="element-2252">
          <code data-id="element-2253">payments.payout.failed</code>
        </li>
      </ul>

      <h3 data-id="element-2254">Wallets</h3>
      <ul className="space-y-1 my-3" data-id="element-2255">
        <li data-id="element-2256">
          <code data-id="element-2257">wallets.wallet.created</code>
        </li>
        <li data-id="element-2258">
          <code data-id="element-2259">wallets.balance.updated</code>
        </li>
        <li data-id="element-2260">
          <code data-id="element-2261">wallets.transfer.created</code>
        </li>
        <li data-id="element-2262">
          <code data-id="element-2263">wallets.transfer.succeeded</code>
        </li>
        <li data-id="element-2264">
          <code data-id="element-2265">wallets.transfer.failed</code>
        </li>
        <li data-id="element-2266">
          <code data-id="element-2267">wallets.ledger_entry.created</code>
        </li>
      </ul>

      <h3 data-id="element-2268">Exchange</h3>
      <ul className="space-y-1 my-3" data-id="element-2269">
        <li data-id="element-2270">
          <code data-id="element-2271">exchange.quote.created</code>
        </li>
        <li data-id="element-2272">
          <code data-id="element-2273">exchange.trade.executed</code>
        </li>
        <li data-id="element-2274">
          <code data-id="element-2275">exchange.trade.settled</code>
        </li>
        <li data-id="element-2276">
          <code data-id="element-2277">exchange.trade.failed</code>
        </li>
      </ul>

      <h3 data-id="element-2278">Compliance</h3>
      <ul className="space-y-1 my-3" data-id="element-2279">
        <li data-id="element-2280">
          <code data-id="element-2281">compliance.verification.started</code>
        </li>
        <li data-id="element-2282">
          <code data-id="element-2283">compliance.verification.completed</code>
        </li>
        <li data-id="element-2284">
          <code data-id="element-2285">compliance.screening.hit</code>
        </li>
        <li data-id="element-2286">
          <code data-id="element-2287">compliance.review.required</code>
        </li>
        <li data-id="element-2288">
          <code data-id="element-2289">compliance.case.closed</code>
        </li>
      </ul>

      <h3 data-id="element-2290">Liquidity</h3>
      <ul className="space-y-1 my-3" data-id="element-2291">
        <li data-id="element-2292">
          <code data-id="element-2293">liquidity.reservation.created</code>
        </li>
        <li data-id="element-2294">
          <code data-id="element-2295">liquidity.reservation.released</code>
        </li>
        <li data-id="element-2296">
          <code data-id="element-2297">liquidity.execution.succeeded</code>
        </li>
        <li data-id="element-2298">
          <code data-id="element-2299">liquidity.execution.failed</code>
        </li>
      </ul>

      <h3 data-id="element-2300">Tokenize</h3>
      <ul className="space-y-1 my-3" data-id="element-2301">
        <li data-id="element-2302">
          <code data-id="element-2303">tokenize.asset.created</code>
        </li>
        <li data-id="element-2304">
          <code data-id="element-2305">tokenize.mint.succeeded</code>
        </li>
        <li data-id="element-2306">
          <code data-id="element-2307">tokenize.transfer.succeeded</code>
        </li>
        <li data-id="element-2308">
          <code data-id="element-2309">tokenize.burn.succeeded</code>
        </li>
        <li data-id="element-2310">
          <code data-id="element-2311">tokenize.operation.failed</code>
        </li>
      </ul>

      <h3 data-id="element-2312">Connect</h3>
      <ul className="space-y-1 my-3" data-id="element-2313">
        <li data-id="element-2314">
          <code data-id="element-2315">connect.api_key.created</code>
        </li>
        <li data-id="element-2316">
          <code data-id="element-2317">connect.webhook_endpoint.updated</code>
        </li>
        <li data-id="element-2318">
          <code data-id="element-2319">connect.integration.health_changed</code>
        </li>
      </ul>

      <h2 id="handling-flow" data-id="element-2320">Recommended Handling Flow</h2>
      <p data-id="element-2321">Follow this pattern to handle webhooks reliably:</p>

      {/* Flow Diagram */}
      <div className="my-8 bg-slate-50 border border-slate-200 rounded-lg p-8" data-id="element-2322">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-2323">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg px-4 py-3 text-center min-w-[140px]" data-id="element-2324">
            <span className="text-sm font-semibold text-blue-900" data-id="element-2325">
              Obseq sends event
            </span>
          </div>
          <ArrowRight className="w-5 h-5 text-slate-400 rotate-90 md:rotate-0" data-id="element-2326" />
          <div className="bg-slate-100 border-2 border-slate-300 rounded-lg px-4 py-3 text-center min-w-[140px]" data-id="element-2327">
            <span className="text-sm font-semibold text-slate-700" data-id="element-2328">
              Your endpoint
            </span>
          </div>
          <ArrowRight className="w-5 h-5 text-slate-400 rotate-90 md:rotate-0" data-id="element-2329" />
          <div className="bg-green-50 border-2 border-green-200 rounded-lg px-4 py-3 text-center min-w-[140px]" data-id="element-2330">
            <span className="text-sm font-semibold text-green-900" data-id="element-2331">
              Return 200 OK
            </span>
          </div>
        </div>
        <div className="flex justify-center mt-6" data-id="element-2332">
          <ArrowRight className="w-5 h-5 text-slate-400 rotate-90" data-id="element-2333" />
        </div>
        <div className="flex justify-center mt-4" data-id="element-2334">
          <div className="bg-purple-50 border-2 border-purple-200 rounded-lg px-4 py-3 text-center" data-id="element-2335">
            <span className="text-sm font-semibold text-purple-900" data-id="element-2336">
              Process asynchronously
            </span>
          </div>
        </div>
      </div>

      <Callout type="warning" data-id="element-2337">
        Obseq retries failed webhooks (non-2xx responses) with exponential
        backoff. Return 2xx quickly to avoid retries.
      </Callout>

      <h2 id="verification" data-id="element-2338">Signature Verification</h2>
      <p data-id="element-2339">Verify webhook authenticity using the signature headers:</p>
      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto my-4" data-id="element-2340">
        <code data-id="element-2341">{`Obseq-Signature: t=1672761600,v1=5f7d8a9b2c3e4f5a6b7c8d9e0f1a2b3c
Obseq-Timestamp: 1672761600`}</code>
      </pre>

      <h3 data-id="element-2342">Verification Steps</h3>
      <ol data-id="element-2343">
        <li data-id="element-2344">
          Extract the timestamp and signature from the{' '}
          <code data-id="element-2345">Obseq-Signature</code> header
        </li>
        <li data-id="element-2346">
          Concatenate: <code data-id="element-2347">timestamp + "." + raw_request_body</code>
        </li>
        <li data-id="element-2348">Compute HMAC-SHA256 using your webhook signing secret</li>
        <li data-id="element-2349">Compare the computed signature with the provided signature</li>
        <li data-id="element-2350">Reject requests with timestamps older than 5 minutes</li>
      </ol>

      <Callout type="note" data-id="element-2351">
        Retrieve your webhook signing secret from the dashboard or Connect API.
        Treat it like an API key.
      </Callout>

      <h2 id="testing" data-id="element-2352">Testing Webhooks</h2>
      <p data-id="element-2353">Test your webhook endpoint before going live:</p>

      <h3 data-id="element-2354">1. Use a Local Tunnel</h3>
      <p data-id="element-2355">Expose your local server using ngrok or similar:</p>
      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg" data-id="element-2356">
        <code data-id="element-2357">ngrok http 3000</code>
      </pre>

      <h3 data-id="element-2358">2. Configure Test Endpoint</h3>
      <p data-id="element-2359">Add your ngrok URL to Obseq (sandbox environment):</p>
      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg" data-id="element-2360">
        <code data-id="element-2361">https://abc123.ngrok.io/webhooks/obseq</code>
      </pre>

      <h3 data-id="element-2362">3. Trigger Test Events</h3>
      <p data-id="element-2363">
        Create test transactions in sandbox to generate webhook events, or use
        the Connect API to send test events.
      </p>

      <h2 id="examples" data-id="element-2364">Code Examples</h2>
      <p data-id="element-2365">Implement webhook handlers in your preferred language:</p>
      <CodeBlock code={webhookHandlerCode} data-id="element-2366" />

      <h3 data-id="element-2367">Best Practices</h3>
      <ul data-id="element-2368">
        <li data-id="element-2369">Return 200 status immediately, before processing</li>
        <li data-id="element-2370">Process events asynchronously using queues or background jobs</li>
        <li data-id="element-2371">Store event IDs to detect and skip duplicates</li>
        <li data-id="element-2372">Log all webhook events for debugging</li>
        <li data-id="element-2373">Implement retry logic for your own processing failures</li>
        <li data-id="element-2374">Monitor webhook endpoint health and latency</li>
      </ul>

      <div className="mt-12 pt-8 border-t border-slate-200" data-id="element-2375">
        <h3 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-2376">
          Related Resources
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2377">
          <a href="#/docs/api-reference/connect" className="p-4 bg-slate-50 border border-slate-200 rounded-lg hover:border-blue-300 transition-colors" data-id="element-2378">
            <h4 className="font-semibold text-slate-900 mb-1" data-id="element-2379">Connect API</h4>
            <p className="text-sm text-slate-600" data-id="element-2380">Manage webhook endpoints</p>
          </a>
          <a href="#/docs/api-conventions" className="p-4 bg-slate-50 border border-slate-200 rounded-lg hover:border-blue-300 transition-colors" data-id="element-2381">
            <h4 className="font-semibold text-slate-900 mb-1" data-id="element-2382">
              API Conventions
            </h4>
            <p className="text-sm text-slate-600" data-id="element-2383">Webhook payload structure</p>
          </a>
        </div>
      </div>
    </DocsLayout>;
}